// Connector tool (spec §5.6): drag from one object (or empty space) to
// another. Ends over an object attach to its nearest side midpoint; ends in
// empty space are free points.
import type { BoardObject, Connector, Endpoint } from '../../shared/types.ts';
import { newId } from '../../shared/ids.ts';
import type { Editor } from '../editor.ts';
import { anchorPoint, connectorPoints, nearestAnchor, pointsBounds, SIDE_ANCHORS } from '../engine/connectors.ts';
import { drawConnector } from '../engine/draw/content.ts';
import type { Tool, WorldPointerEvent } from './types.ts';

const CLICK_PX = 4;

export function endpointAt(ed: Editor, x: number, y: number, exclude: ReadonlySet<string>): { ep: Endpoint; target: BoardObject | null } {
  const target = ed.attachableAt(x, y, exclude);
  return target ? { ep: { objectId: target.id, anchor: nearestAnchor(target, x, y) }, target } : { ep: { point: [x, y] }, target: null };
}

export class ConnectorTool implements Tool {
  cursor = 'crosshair';
  private readonly ed: Editor;
  private drag: { from: Endpoint; fromId: string | null; to: Endpoint; sx: number; sy: number } | null = null;

  constructor(editor: Editor) {
    this.ed = editor;
  }

  onPointerDown(e: WorldPointerEvent): void {
    const { ep, target } = endpointAt(this.ed, e.x, e.y, new Set());
    this.drag = { from: ep, fromId: target?.id ?? null, to: { point: [e.x, e.y] }, sx: e.sx, sy: e.sy };
  }

  onPointerMove(e: WorldPointerEvent): void {
    const ed = this.ed;
    const exclude = new Set(this.drag?.fromId ? [this.drag.fromId] : []);
    const { ep, target } = endpointAt(ed, e.x, e.y, exclude);
    if (this.drag) this.drag.to = ep;
    ed.attachTarget = target?.id ?? null;
    ed.renderer.invalidateInteractive();
  }

  onPointerUp(e: WorldPointerEvent): void {
    const d = this.drag;
    this.drag = null;
    const ed = this.ed;
    ed.attachTarget = null;
    ed.renderer.invalidateInteractive();
    if (!d || Math.hypot(e.sx - d.sx, e.sy - d.sy) < CLICK_PX) return;
    const conn = this.preview(d.from, d.to);
    ed.apply([{ t: 'create', obj: conn }]);
    ed.setTool('select');
    ed.setSelection([conn.id]);
  }

  onCancel(): void {
    this.drag = null;
    this.ed.attachTarget = null;
    this.ed.renderer.invalidateInteractive();
  }

  private preview(from: Endpoint, to: Endpoint): Connector {
    const ed = this.ed;
    const c: Connector = {
      id: newId(), type: 'connector', x: 0, y: 0, w: 0, h: 0, rotation: 0,
      z: ed.zKeysOnTop(1)[0]!, createdBy: ed.userId,
      from, to, routing: ed.connectorRouting, stroke: '#1A1A1A', arrowStart: false, arrowEnd: true,
    };
    // Stored bounds are only a fallback for ends whose object disappears.
    const b = pointsBounds(connectorPoints(c, (id) => ed.store.get(id)), 0);
    return { ...c, x: b.x, y: b.y, w: b.w, h: b.h };
  }

  drawOverlay(ctx: CanvasRenderingContext2D): void {
    const ed = this.ed;
    const zoom = ed.camera.zoom;
    // Anchor dots on the object an end would attach to.
    const target = ed.attachTarget ? ed.store.get(ed.attachTarget) : null;
    if (target) {
      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = '#4262FF';
      ctx.lineWidth = 1.5 / zoom;
      for (const a of SIDE_ANCHORS) {
        const [x, y] = anchorPoint(target, a);
        ctx.beginPath();
        ctx.arc(x, y, 4 / zoom, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
    if (!this.drag) return;
    const c = this.preview(this.drag.from, this.drag.to);
    drawConnector(ctx, { ...c, stroke: '#4262FF' }, connectorPoints(c, (id) => ed.store.get(id)), false, zoom * ed.layers.dpr);
  }
}
